import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createReportSummaryVideoSkill: Skill = {
    name: SKILL_NAMES.createReportSummaryVideo,
    description:
        'A small number of dense, informative static slides set to music with no voiceover, closing on a call to action pointing to a link. Use for summarizing reports, research, survey results or longform documents into their key findings and statistics.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Report Summary',
        preview: 'report-summary',
        frames: [
            'report-summary-1',
            'report-summary-2',
            'report-summary-3',
            'report-summary-4',
            'report-summary-5',
        ],
    },
    instructions: `# Create Report Summary Video
For condensing a longform written piece — a report, a research summary, an
article — into a short deck of **informative, static slides**, generally
starting around **10-30 seconds**
but free to run longer when there's genuinely more to say. Built in three
parts: a title/subtitle open, a middle section packing in one key idea and
as much of its real supporting detail as a slide can legibly hold, and a
call-to-action close pointing to a specific link. Each scene should read
like a slide someone could pause on and actually learn something from, not
a quick flashing beat — favor fewer, fuller, longer-held slides dense with
real information over many thin, quick-cut ones. Music plays throughout;
there is no voiceover.

## Setup
Use \`${SKILL_NAMES.gatherRequirements}\` to get necessary info, skipping anything already
provided. Use that skill's general suggestions, but also specifically make sure we get:
- The source content — the longform article or document to condense, or a
  link to it. This is required to build anything
- The call to action — the exact CTA text and the specific link it should
  point to, for the closing scene

Two overrides to that skill for this format:
- **Aspect ratio**: ask it as usual (the picker takes no default), but if the
  user doesn't pick one, apply 4:5 with \`${TOOL_NAMES.setAspectRatio}\` — a
  social-native near-square suits this format. Set it before building any scenes.
- **Don't ask about voiceover.** This format is music-only.

Show users options of templates that suit social-feed content. Search if none of the
available ones seem an obvious fit.

## Narration and music
- **No voiceover, ever.** This format is music-only — there is no narration
  track at all.
- **Music must actually end up in the video.** Search \`${TOOL_NAMES.searchMusic}\` for a
  track that grabs attention in the first second or two (this is competing
  for a scroll) while still matching the tone of the source
  content — upbeat and punchy for a lighter piece, more restrained and
  serious for weighty subject matter. Apply the chosen track to the video
  (don't stop at searching) and set its volume so it energizes without
  drowning out the on-screen text's presence. Before moving on, confirm with
  \`${TOOL_NAMES.getVideoState}\` that a music track is actually attached — a finished
  video with no music applied is a defect.

## Content — condensed, not verbatim, but faithful
- **This is a condensation task: real paraphrasing and trimming are expected
  and necessary** to distill a longform piece into a short slide deck —
  unlike some other recipes in this set, the words on screen do not need to
  be copied verbatim from the source.
- **Every claim still has to be faithful to what the source actually says.**
  Condensing to fit the runtime is fine; changing, exaggerating, or
  inventing a point that isn't genuinely in the source is not. If you're
  unsure whether a compressed phrasing still represents the source
  accurately, err toward including more context rather than a catchier but
  looser paraphrase.
- **The title and subtitle in Part 1 should reflect the source's actual
  title/headline and its real subheading or opening framing** — not an
  invented hook. Light tightening for space is fine; substituting a made-up
  headline is not.

## Structure — three parts, built as a small deck of dense slides
- **Part 1 — title and subtitle (one scene).** A Title-category scene
  showing the source piece's actual title and subtitle. This is the one
  scene that should still be quick — just long enough to read the title and
  subtitle, setting up what's coming.
- **Part 2 — one key idea, packed with as much real supporting detail as a
  slide can hold.** Identify the single most important takeaway of the
  source piece and lead with it, then load the slide(s) with the genuine
  supporting detail behind it — not just a couple of bullet points, but the
  real substance: the specific numbers, the named examples, the concrete
  reasons, whatever the source actually backs the idea up with. **Think of
  each Part 2 scene as a static, information-dense slide the viewer pauses
  on to actually read and absorb** — closer to an infographic panel than a
  quick social flash-cut. Favor packing more onto one well-designed slide
  (a Lists design holding a full set of points, a Data design carrying
  several stats with context) over spreading thin content across several
  scenes. Only add a second Part 2 scene when there's genuinely more
  substantive content than one slide can legibly hold — not to create
  visual variety, and not because a scene "feels long."
- **Part 3 — call to action (one scene).** A scene carrying the exact CTA
  text and link gathered in Setup, pointing the viewer to visit it. This is
  the last thing on screen; never end anywhere else.
- **Duration is a byproduct of the content, not a cap to write down to.**
  10-30 seconds total is a reasonable starting expectation, but this format
  now prioritizes packing in real information over hitting a short runtime
  — if a slide genuinely needs more time to hold and read its content
  comfortably, let the video run longer rather than thinning out the
  content or rushing the reading pace to stay under 30 seconds. Don't pad
  with filler to stretch it, either — the length should track how much
  substantive, faithful content the source actually supports.

## Scene design
- Match each part's design to what it's actually showing: Part 1 is a Title
  design; Part 2 reaches for whatever category fits its content (Lists for
  several short supporting points, Data/chart for a number worth seeing,
  Text or Quote for a single strong line, Icon for a short set of concepts);
  Part 3 is a CTA-appropriate design that makes the link/action clearly
  legible.
- With so few scenes in a video this short, don't force variety for its own
  sake — but don't default to the same design category for Part 1 and Part
  2 either if a better-fitting option exists for each.

## Asset timing — no voiceover, so timing is set by reading time
- **Every scene's duration is driven by how long its text takes to
  comfortably read, not by spoken word timing** — there is no voiceover to
  anchor to. Use \`${TOOL_NAMES.setSceneDuration}\` (auto mode, when available, sizes to
  the actual text length) or set a manual duration long enough for a
  comfortable read; check against \`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\` to
  confirm each scene is neither too short to read nor needlessly long.
- **A longer scene is not a problem in this format — it's expected.** Part 2
  scenes are meant to function as dense, static, information-rich slides
  (see Structure above), and a slide with real substance on it legitimately
  needs more time to read than a quick one-liner. Give those scenes whatever
  duration their actual content needs to be read comfortably and fully
  absorbed, even if that's well beyond what a typical quick-cut social clip
  would use — never force a short duration onto a scene that's genuinely
  carrying a lot of information.

## Media
- Media is optional in this format — Part 2 in particular can be a strong
  text/list/data design with no media at all if nothing in the source
  clearly supports a visual. **Only include media when it obviously fits a
  specific point** — a vaguely-related stock clip is worse than no media, so
  don't force one in just to fill a scene.
- If media is warranted, load \`${SKILL_NAMES.findStockMedia}\` before your first stock
  search, and \`${SKILL_NAMES.findDamMedia}\` too when a DAM is connected — brand or
  company content likely lives there and may fit better than generic
  stock.
- Reframe within the asset so the subject is well-composed and nothing
  important sits under text (\`${SKILL_NAMES.cropMedia}\` skill).

## Styling
- Colors and styling come from the template or the user's brand kit — apply a
  kit when one exists or the user references their brand (\`${SKILL_NAMES.applyBrandKit}\`
  skill); don't hand-pick color overrides unprompted.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm:
- **Runtime tracks the actual content rather than being forced under 30
  seconds** — 10-30 seconds is a starting expectation, not a hard ceiling;
  a video that runs longer because its slides are genuinely packed with
  real information is correct, not a defect. Check the other direction too:
  it shouldn't be long because of padding or a slow pace, only because of
  real content.
- **Part 1's title and subtitle genuinely reflect the source piece's actual
  title and framing** — not an invented hook.
- **Part 2's key idea and its supporting detail are faithful to the
  source** — condensed and paraphrased as needed, but nothing fabricated,
  exaggerated, or misrepresented.
- **Part 2 genuinely reads as a dense, informative slide, not a thin
  highlight.** Check that it's carrying real substance — specific numbers,
  named examples, concrete reasons from the source — not just a headline
  and a couple of vague bullets. If it split into several thin scenes each
  holding only a sliver of content, check whether that content actually
  belongs together on fewer, fuller slides instead.
- **Part 3 shows the exact CTA text and link gathered in Setup**, as the
  final scene in the video.
- **Confirm a music track is actually applied** (\`${TOOL_NAMES.getVideoState}\`) and that
  there is no voiceover track anywhere in the video.
- **Check each scene's duration against its actual reading time**
  (\`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\`) — nothing flashing by too fast to
  read given how much a dense Part 2 slide can carry, and nothing lingering
  past what its text actually needs either.
- The aspect ratio matches what the user confirmed in Setup.
- Any media included obviously fits its point — no awkward or vaguely
  related clips forced in.`,
};
